import { Box, Button, Dialog, DialogTitle } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Widget } from '~/components/MyWorkspace/Widget';
import { NullableTooltip } from '~/components/NullableTooltip';
import { useVisible } from '~/hooks/useVisible';

import { AllListDialogContentComponentFactory } from './components/AllListDialogContent';
import { ContentComponentFactory } from './components/Content';
import { useSxStyles } from './useStyles';

export interface ListItemComponentProps<LI, LICustomActions> {
  listItem: LI;
  isTabDisabled?: boolean;
  customActions?: LICustomActions;
}

export interface ListItemInfoComponentProps<LI> {
  listItem: LI;
}

export interface Props<LI, LICustomActions> {
  title: string;
  data?: LI[];
  loading: boolean;
  failedToLoad: boolean;
  noDataMessage: string;
  onAddListItemClick?: () => void;
  addListItemUrl?: string;
  isTabDisabled?: boolean;
  customActions?: LICustomActions;
}

type ListWidgetComponentFactoryPropsType<LI, LICustomActions> = {
  ListItemComponent: React.ComponentType<
    ListItemComponentProps<LI, LICustomActions>
  >;
  getIdentifier: (item: LI) => string | number;
  ListItemInfoComponent?: React.ComponentType<ListItemInfoComponentProps<LI>>;
  TitleComponent?: React.ComponentType<{ title: string; listItems?: LI[] }>;
  dataCy?: string;
};

export const ListWidgetComponentFactory = <
  LI extends {},
  LICustomActions extends {},
>(
  props: ListWidgetComponentFactoryPropsType<LI, LICustomActions>,
) => {
  const {
    ListItemComponent,
    getIdentifier,
    ListItemInfoComponent,
    TitleComponent,
    dataCy,
  } = props;

  const Content = ContentComponentFactory({
    ListItemComponent,
  });
  const AllListItemsDialogContent = AllListDialogContentComponentFactory({
    ListItemComponent,
    getIdentifier,
    ListItemInfoComponent,
  });

  const Component: React.FC<Props<LI, LICustomActions>> = ({
    title,
    data,
    loading,
    failedToLoad,
    noDataMessage,
    onAddListItemClick,
    addListItemUrl,
    isTabDisabled,
    customActions,
  }) => {
    const { t } = useTranslation();
    const sxStyles = useSxStyles();

    const {
      isVisible: isAllListDialogOpen,
      show: openAllListDialog,
      hide: closeAllListDialog,
    } = useVisible();

    const [noOfFittingListItems, setNoOfFittingListItems] = useState<number>(0);

    const { isAllListDialogDisabled } = useMemo(() => {
      const itemCount = (!!data && data.length) || 0;

      return {
        hiddenItemCount:
          itemCount > noOfFittingListItems
            ? itemCount - noOfFittingListItems
            : 0,
        isAllListDialogDisabled: !data || !(itemCount > 0),
      };
    }, [data, noOfFittingListItems]);

    return (
      <>
        <Widget
          title={
            (TitleComponent && (
              <TitleComponent title={title} listItems={data} />
            )) ||
            title
          }
          content={
            <Content
              listItemsLoading={loading}
              listItems={data?.slice(0, noOfFittingListItems)}
              failedToLoadListItems={failedToLoad}
              noDataMessage={noDataMessage}
              getIdentifier={getIdentifier}
              onResize={setNoOfFittingListItems}
              isTabDisabled={isTabDisabled}
              customActions={customActions}
            />
          }
          actions={
            <>
              <Button
                size="small"
                color="primary"
                disabled={isAllListDialogDisabled}
                onClick={openAllListDialog}
                data-cy="show-all"
              >
                {t('showAll')}
              </Button>
              {(onAddListItemClick || addListItemUrl) && (
                <NullableTooltip
                  title={
                    isTabDisabled ? t<string>('disabledFunctionality') : ''
                  }
                >
                  <Box sx={sxStyles.tooltipContainer}>
                    <Button
                      size="small"
                      color="primary"
                      onClick={onAddListItemClick}
                      href={addListItemUrl}
                      disabled={isTabDisabled}
                      data-cy="add"
                    >
                      {t('add')}
                    </Button>
                  </Box>
                </NullableTooltip>
              )}
            </>
          }
          dataCy={dataCy}
        />

        <Dialog
          open={isAllListDialogOpen}
          onClose={closeAllListDialog}
          data-cy="all-list-widget-dialog"
        >
          <DialogTitle>{title}</DialogTitle>
          <AllListItemsDialogContent
            listItems={data}
            isTabDisabled={isTabDisabled}
            customActions={customActions}
          />
        </Dialog>
      </>
    );
  };

  return Component;
};
